import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { TeamScore } from "../types";

/**
 * Hook to fetch all teams with their scores for the active weekend
 */
export function useTeamScores() {
  const { activeWeekend } = useAuth();
  const [teams, setTeams] = useState<TeamScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch teams with scores and member counts
   */
  const fetchTeams = useCallback(async () => {
    if (!activeWeekend?.id) {
      setTeams([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch teams for the active weekend
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select(`
          id,
          name,
          color,
          avatar_url,
          description,
          total_score,
          weekend_id
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("total_score", { ascending: false });

      if (teamsError) throw teamsError;

      if (!teamsData || teamsData.length === 0) {
        setTeams([]);
        return;
      }

      // Fetch member counts for each team
      const { data: memberCounts, error: memberError } = await supabase
        .from("team_members")
        .select("team_id")
        .in(
          "team_id",
          teamsData.map((t) => t.id)
        );

      if (memberError) throw memberError;

      // Count members per team
      const memberCountMap: Record<string, number> = {};
      (memberCounts || []).forEach((m) => {
        memberCountMap[m.team_id] = (memberCountMap[m.team_id] || 0) + 1;
      });

      // Map teams with ranks and member counts
      const rankedTeams: TeamScore[] = teamsData.map((team, index) => ({
        id: team.id,
        name: team.name,
        color: team.color,
        avatar_url: team.avatar_url,
        description: team.description,
        total_score: team.total_score || 0,
        weekend_id: team.weekend_id,
        member_count: memberCountMap[team.id] || 0,
        rank: index + 1,
      }));

      setTeams(rankedTeams);
    } catch (err) {
      console.error("Error fetching team scores:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch teams");
    } finally {
      setLoading(false);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  /**
   * Get the user's team position
   */
  const getUserTeamPosition = useCallback(
    (teamId: string | undefined): number | null => {
      if (!teamId) return null;
      const team = teams.find((t) => t.id === teamId);
      return team?.rank || null;
    },
    [teams]
  );

  /**
   * Get the next goal for a team (score of the team above)
   */
  const getNextGoal = useCallback(
    (teamId: string | undefined): number | null => {
      if (!teamId) return null;
      const teamIndex = teams.findIndex((t) => t.id === teamId);
      if (teamIndex <= 0) {
        // Team is first or not found - next goal is current + 500
        const team = teams.find((t) => t.id === teamId);
        return team ? Math.ceil((team.total_score + 100) / 100) * 100 : 500;
      }
      // Return the score of the team above
      return teams[teamIndex - 1].total_score;
    },
    [teams]
  );

  return {
    teams,
    loading,
    error,
    refresh: fetchTeams,
    getUserTeamPosition,
    getNextGoal,
    topTeams: teams.slice(0, 3),
  };
}

